import { notEmpty, parseQueryStrings } from '@crehana/utils';

import { CountryPrefix, SubCategory } from 'Jsx/global.d';

import type { Filters, FilterSubitem, OrderBy } from '../../types';

/* ========================== CATEGORIES ========================= */
export const getCategorySelected = ({
  categories = {},
  categorySelected: categoryInfo,
}: Filters) => {
  // const t0 = performance.now();

  const categorySelected = categories[categoryInfo?.itemId!];
  const subCategoriesSelected = categorySelected
    ? Object.keys(categorySelected.subItems).reduce(
        (arr: FilterSubitem<SubCategory>[], key) => {
          const currentSubItem = categorySelected.subItems[key]!;

          if (
            currentSubItem.id === categoryInfo.subItemId &&
            currentSubItem.id !== 0
          ) {
            arr.push(currentSubItem);
          }

          return arr;
        },
        [],
      )
    : [];

  // const t1 = performance.now();

  // console.log(`${t0} to ${t1}> Call getCategorySelected took: ${t1 - t0} miliseconds`);
  return {
    categorySelected,
    subCategoriesSelected,
  };
};

/* ========================== SOFTWARES ========================= */

export const getSoftwaresSelectedIds = ({ softwaresSelected }: Filters) =>
  Object.values(softwaresSelected)
    .map(s => s?.id)
    .filter(notEmpty);

export const getSoftwaresSelected = (filters: Filters) =>
  Object.values(filters.softwaresSelected).filter(notEmpty);

/* ========================== LEVELS ========================= */

export const getLevelsSelectedIds = ({ levelsSelected }: Filters) =>
  Object.values(levelsSelected)
    .map(level => level?.id)
    .filter(notEmpty);

export const getLevelsSelected = (filters: Filters) =>
  Object.values(filters.levelsSelected).filter(notEmpty);

export const isFilterApplied = (filters: Filters) => {
  const isApplied =
    !!getCategorySelected(filters).categorySelected ||
    !!(getSoftwaresSelected(filters).length > 0) ||
    !!(getLevelsSelected(filters).length > 0);

  return isApplied;
};

/** *************** Language **************** */
export const getSelectedLanguageIds = ({ selectedLanguages }: Filters) =>
  Object.values(selectedLanguages).map(val => val.id);

export const getSelectedLanguages = (filters: Filters) =>
  Object.values(filters.selectedLanguages).filter(notEmpty);

export const getSelectedDurations = (filters: Filters) =>
  filters.duration.selectedTags;

// SEO HELPERS
export const getUrl = ({
  countryPrefix,
  filters,
  orderBy,
}: {
  countryPrefix: CountryPrefix;
  filters: Filters;
  orderBy: OrderBy;
}) => {
  const firstPosition = 'cursos';
  const secondPosition = orderBy?.itemSelected?.value;

  const { categorySelected, subCategoriesSelected } =
    getCategorySelected(filters);
  const softwaresSelected = getSoftwaresSelected(filters);
  const levelsSelected = getLevelsSelected(filters);
  const selectedLanguages = getSelectedLanguages(filters);
  const selectedDurations = getSelectedDurations(filters);

  const allCoursesParam = filters.allCourses;
  let thirdPositionCategory = filters.categorySelected?.slug || '';
  let thirdPositionSoftware = '';
  const queryStrings = parseQueryStrings(window.location.search) || {};

  if (subCategoriesSelected.length > 0) {
    thirdPositionCategory =
      subCategoriesSelected?.[0]?.slug || thirdPositionCategory;
    if (subCategoriesSelected.length > 1) {
      queryStrings.subCategories = subCategoriesSelected
        .map(sc => sc.slug)
        .join(',');
    }
  } else if (categorySelected) {
    if ('subCategories' in queryStrings) {
      delete queryStrings.subCategories;
    }
  } else if ('subCategories' in queryStrings) {
    delete queryStrings.subCategories;
  }

  if (softwaresSelected.length > 0) {
    if (softwaresSelected.length > 1 || thirdPositionCategory) {
      queryStrings.software = softwaresSelected.map(s => s.slug).join(',');
    } else {
      thirdPositionSoftware = softwaresSelected[0]?.slug ?? '';
    }
  } else if ('software' in queryStrings) {
    delete queryStrings.software;
  }

  if (levelsSelected.length > 0) {
    queryStrings.nivel = levelsSelected.map(l => l.slug).join(',');
  } else if ('nivel' in queryStrings) {
    delete queryStrings.nivel;
  }

  if (selectedLanguages.length) {
    queryStrings.idiomas = selectedLanguages
      .map(selectedLang => selectedLang.slug)
      .join(',');
  } else if ('idiomas' in queryStrings) {
    delete queryStrings.idiomas;
  }

  if (selectedDurations.length) {
    queryStrings.duraciones = selectedDurations.join(',');
  } else if ('duraciones' in queryStrings) {
    delete queryStrings.duraciones;
  }

  if (allCoursesParam) {
    queryStrings.all = true;
  }

  const queryParams = Object.entries(queryStrings).map(
    ([key, val]) => `${key}=${val}`,
  );

  const baseUrl = `${firstPosition}-${secondPosition}`;
  const thirdPosition = thirdPositionCategory || thirdPositionSoftware;
  const restUrl = thirdPosition ? `-${thirdPosition}` : '';
  const queryUrl = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  const prefix =
    countryPrefix && countryPrefix.toLowerCase
      ? `/${countryPrefix.toLowerCase()}`
      : '';

  return `${prefix}/${baseUrl}${restUrl}/${queryUrl}`;
};
